import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { PropsType } from '../../models/util'
import moment from 'moment'
import LuxButton from '../buttons/Button'
import { TEMPLATE_TYPE } from '../../models/data'
import { LuxDropdownMenu } from '../../Techedge-Wardrobe'
import { Tooltip } from 'primereact/tooltip'
import LuxIcon from '../icons/LuxIcon'

export interface ColumnProps {
    field: string // field name, used to match the property of a dataset row
    header?: string | Element // column header, can be a title or a custom template
    colWidth?: number // number of pixel for the header width, default: auto
    textAlign?: string // left|right|center|justify|initial|inherit
    template?: TEMPLATE_TYPE | Element //  can be a predefined template or a custom template
    sortable?: boolean // enables sort button in column header
    className?: string
    editor?: (props: any) => Element // makes the cells editable with specified input (select, text, ...)
    editorValidator?: (props: any) => boolean // makes the cells editable with specified input (select, text, ...)
    tooltip?: string
    sticky?: boolean
    style?: any
    columnKey?: string
}

export interface DataTableProps extends PropsType {
    dataSet: any[]
    columns: ColumnProps[]
    actionsList?: {
        icon: string
        command: (any) => void
        tooltip?: string
        tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
        label?: string
        className?: string
        variant?: string[]
        checkDisabled?: (any) => boolean
        disabled?: boolean
    }[]
    selectedRows?: any[]
    setSelectedRows?: (any) => void
    useSpeedDial?: boolean
    dateFormat?: string
    inputDateFormat?: string
    rows?: number
    setRows?: (number) => void
    statusList?: any
    labels?: { emptyMessage?: string }
    page?: number
    setPage?: (number) => void
    first?: number
    currentPageReport?: boolean
    rowsPerPageOptions?: number[]
    onPage?: (any) => void
    lazy?: boolean
    totalRecords?: number
    onSort?: (any) => void
    sortField?: string
    onAllRowsSelect?: (e) => void
    onAllRowsUnselect?: (e) => void
    sortOrder?: any
    showTooltips?: boolean
    checkboxSelection?: boolean
    editMode?: string // 'cell', TODO: support row edit mode
    dataKey?: string
    scrollable?: boolean
    id?: string
    containerClassName?: string
}

const LuxDataTable = (props: DataTableProps) => {
    const {
        classes,
        onAllRowsSelect,
        onAllRowsUnselect,
        dataSet,
        columns,
        actionsList,
        useSpeedDial,
        rows,
        dateFormat,
        inputDateFormat,
        statusList,
        labels,
        page,
        first,
        currentPageReport,
        dataKey,
        rowsPerPageOptions,
        onPage,
        lazy,
        totalRecords,
        onSort,
        sortField,
        sortOrder,
        showTooltips,
        checkboxSelection,
        editMode,
        style,
        scrollable,
        selectedRows,
        setSelectedRows,
        id,
        containerClassName,
    } = props

    const [pageState, setPageState] = useState(0)

    useEffect(() => {
        if (page || page === 0) setPageState(page)
    }, [page])

    const statusTemplate = (rowData) => {
        let status = statusList?.filter((s) => s.value == rowData.status)[0]

        return (
            <div
                className={`status-badge`}
                style={{
                    backgroundColor: status?.backgroundColor
                        ? status.backgroundColor
                        : 'var(--lux-tertiary)',
                    color: status?.color ? status.color : '',
                    border: status?.border ? status.border : '',
                    borderRadius: status?.borderRadius
                        ? status.borderRadius
                        : '',
                }}>
                {status?.label}
            </div>
        )
    }

    const _dateFormat = dateFormat ? dateFormat : 'YYYY/MM/DD'
    const dateRangeTemplate = (rowData, field) => {
        if (rowData[`${field}_start`] && rowData[`${field}_end`])
            return (
                <div>
                    {moment
                        .utc(rowData[`${field}_start`], inputDateFormat)
                        .format(_dateFormat)}
                    -
                    {moment
                        .utc(rowData[`${field}_end`], inputDateFormat)
                        .format(_dateFormat)}
                </div>
            )
        else return '---'
    }

    const dateTemplate = (rowData, field) => {
        if (rowData[field])
            return (
                <div>
                    {moment
                        .utc(rowData[field], inputDateFormat)
                        .format(_dateFormat)}
                </div>
            )
        else return '---'
    }

    const menu = useRef(null)
    const actionsTemplate = (rowData) => {
        if (useSpeedDial) {
            return (
                <>
                    {actionsList?.length > 1 ? (
                        <LuxDropdownMenu
                            text=""
                            icon="ellipsis-v"
                            variant={['icon']}
                            items={actionsList.map((a: any) => ({
                                icon: a.icon,
                                className: a.className,
                                label: a.label || a.tooltip,
                                command: () => a.command(rowData),
                                disabled: a.checkDisabled
                                    ? a.checkDisabled(rowData)
                                    : false,
                            }))}
                        />
                    ) : (
                        <LuxButton
                            variant={['icon']}
                            icon={actionsList[0].icon}
                            onClick={actionsList[0].command}
                            tooltip={actionsList[0].tooltip}
                            tooltipPosition={
                                actionsList[0].tooltipPosition
                                    ? actionsList[0].tooltipPosition
                                    : 'left'
                            }
                        />
                    )}
                </>
            )
        } else
            return (
                <div className="actions-container">
                    {actionsList?.length &&
                        actionsList?.map((f, i) => {
                            return (
                                <LuxButton
                                    key={i}
                                    variant={['icon'].concat(f.variant)}
                                    classes={f.className}
                                    icon={f.icon}
                                    onClick={() => f.command(rowData)}
                                    tooltip={f.tooltip}
                                    tooltipPosition={
                                        f.tooltipPosition
                                            ? f.tooltipPosition
                                            : 'left'
                                    }
                                    disabled={
                                        f.disabled || f.checkDisabled
                                            ? f.checkDisabled(rowData)
                                            : false
                                    }
                                />
                            )
                        })}
                </div>
            )
    }

    const template = (template: string | Element, field: string) => {
        // if (field === 'printDescription') debugger
        switch (template) {
            case TEMPLATE_TYPE.status:
                return (rowData: any) => statusTemplate(rowData)
            case TEMPLATE_TYPE.actions:
                return (rowData: any) => actionsTemplate(rowData)
            case TEMPLATE_TYPE.date_range:
                return (rowData: any) => dateRangeTemplate(rowData, field)
            case TEMPLATE_TYPE.date:
                return (rowData: any) => dateTemplate(rowData, field)
            default:
                // custom template
                return template
        }
    }

    const headerStyle = (f: ColumnProps) => {
        if (f.field == 'actions')
            return {
                width: `${40 * actionsList?.length + 32}px`,
                textAlign: 'center',
            }
        else
            return {
                width: f.colWidth ? `${f.colWidth}px` : 'auto',
                textAlign: f.textAlign ? f.textAlign : 'left',
            }
    }

    let frozenWidth = 0
    if (scrollable && columns?.length) {
        columns.forEach((c) => {
            if (c.sticky && c.colWidth) {
                if (c.field == 'actions')
                    frozenWidth += 40 * actionsList?.length + 32
                else frozenWidth += c.colWidth
            }
        })
        if (checkboxSelection) {
            frozenWidth += 52
        }
    }

    let stickyColumns = 0
    if (columns?.length > 0) {
        for (let i = 0; i < columns.length; i++) {
            let column = columns[i]
            if (column?.sticky) {
                stickyColumns++
            }
        }
    }

    return (
        <div className={`${containerClassName ? containerClassName : ''}`}>
            <div className="card table-card luxDataTable">
                <DataTable
                    id={id}
                    onAllRowsSelect={onAllRowsSelect}
                    onAllRowsUnselect={onAllRowsUnselect}
                    editMode={editMode}
                    style={style}
                    frozenWidth={`${frozenWidth + 1}px`}
                    scrollable={scrollable}
                    selectionMode={checkboxSelection ? 'checkbox' : null}
                    selection={selectedRows}
                    onSelectionChange={(e) => setSelectedRows(e.value)}
                    dataKey={dataKey ? dataKey : 'id'}
                    className={classes}
                    first={first || pageState}
                    value={dataSet}
                    onPage={onPage}
                    onSort={onSort}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    paginator
                    paginatorTemplate={`${
                        currentPageReport ? 'CurrentPageReport' : ''
                    } FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown`}
                    currentPageReportTemplate={
                        currentPageReport
                            ? '{first} - {last} of {totalRecords}'
                            : undefined
                    }
                    rows={rows ? rows : 15}
                    rowsPerPageOptions={
                        rowsPerPageOptions ? rowsPerPageOptions : undefined
                    }
                    lazy={lazy || false}
                    totalRecords={totalRecords}
                    emptyMessage={
                        labels?.emptyMessage
                            ? labels.emptyMessage
                            : 'No results to show.'
                    }
                    onRowClick={(e) => e.originalEvent.preventDefault()}
                    rowClassName={(rowData?: any) => ({
                        'p-row-status-draft': rowData?.status === 'draft',
                    })}
                    // expandable row groups
                    // rowGroupMode="subheader"
                    // expandableRowGroups={rowGroup ? true : false}
                    // expandedRows={expandedRows}
                    // onRowToggle={(e) => setExpandedRows(e.data)}
                    // onRowExpand={this.onRowGroupExpand}
                    // onRowCollapse={this.onRowGroupCollapse}
                    // groupField={rowGroup?.field}
                    // rowGroupHeaderTemplate={
                    //     rowGroup?.headerTemplate
                    //         ? rowGroup?.headerTemplate
                    //         : () => <></>
                    // }
                    // rowGroupFooterTemplate={() => <></>}
                >
                    {checkboxSelection && (
                        <Column
                            key={'checkbox_column'}
                            selectionMode="multiple"
                            frozen={true}
                            className="checkbox-column"
                        />
                    )}
                    {columns?.length &&
                        columns.map((f, i) => {
                            // debugger
                            let _body = template(f.template, f.field)
                            // add edit icon to editable cells
                            if (f.editor) {
                                _body = (rowData) => (
                                    <div className="editable-cell">
                                        {f.template
                                            ? template(f.template, f.field)
                                            : rowData[f.field]}
                                        <LuxIcon
                                            size={14}
                                            icon="pencil"
                                            classes="edit-icon"
                                            color="var(--lux-primary)"
                                        />
                                    </div>
                                )
                            }
                            return (
                                <Column
                                    key={i}
                                    field={f.field}
                                    header={f.header}
                                    headerStyle={headerStyle(f)}
                                    body={_body}
                                    style={f.style}
                                    sortable={f.sortable}
                                    className={f.className}
                                    editor={f.editor}
                                    editorValidator={f.editorValidator}
                                    frozen={f.sticky}
                                    columnKey={f.columnKey}
                                    // style={
                                    //     f.template == 'actions' && !useSpeedDial
                                    //         ? {
                                    //               width: `${
                                    //                   actionsList.length * 55
                                    //               }px`,
                                    //           }
                                    //         : undefined
                                    // }
                                />
                            )
                        })}
                </DataTable>
                {showTooltips &&
                    columns?.length > 0 &&
                    columns.map((f, i) => {
                        let child =
                            i +
                            (checkboxSelection ? 2 : 1) -
                            (f?.sticky
                                ? 0
                                : checkboxSelection
                                ? stickyColumns + 1
                                : stickyColumns)
                        let target = id ? '.p-datatable[id="' + id + '"] ' : ''
                        target += scrollable
                            ? f?.sticky
                                ? '.p-datatable-frozen-view '
                                : '.p-datatable-unfrozen-view '
                            : ''
                        return (
                            <Tooltip
                                key={i}
                                position={'top'}
                                at={'left top'}
                                my={'left top-25'}
                                target={
                                    target +
                                    'th[role="columnheader"]:nth-child(' +
                                    child +
                                    ') .p-column-title'
                                }
                                content={
                                    f.tooltip?.toString() ||
                                    f.header?.toString() ||
                                    f.field
                                }
                            />
                        )
                    })}
            </div>
        </div>
    )
}

export default LuxDataTable
